import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';

type ConfigPublica = {
  id_creche: number;
  nome_assistente: string;
  mensagem_boas_vindas_publica: string;
  horario_funcionamento?: string;
  formas_atuacao?: string;
  conteudo_site_publico?: string;
  contato_publico?: string;
  topicos_publicos_liberados?: string[];
  audio_entrada_publico?: boolean;
  audio_saida_publico?: boolean;
  voz_publica?: string;
  creche?: {
    nome?: string;
    endereco?: string;
    cidade?: string;
    estado?: string;
    telefone?: string;
    email?: string;
  };
  cardapios?: Array<{
    id: number;
    dia_semana?: string;
    cafe_manha?: string;
    almoco?: string;
    lanche_tarde?: string;
    jantar?: string;
  }>;
  materiais_publicos?: Array<{
    id: number;
    titulo: string;
    descricao?: string;
    tipo?: string;
    origem?: string;
  }>;
};

type Mensagem = {
  id: number;
  papel: 'usuario' | 'assistente';
  texto: string;
};

type LiveTokenResponse = {
  provider: string;
  model: string;
  access_token: string;
  expire_time?: string;
  new_session_expire_time?: string;
  ws_url: string;
  setup?: any;
};

declare global {
  interface Window {
    SpeechRecognition?: any;
    webkitSpeechRecognition?: any;
  }
}

export default function PublicHomePage() {
  const [config, setConfig] = useState<ConfigPublica | null>(null);
  const [mensagens, setMensagens] = useState<Mensagem[]>([]);
  const [pergunta, setPergunta] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [ouvindo, setOuvindo] = useState(false);
  const [liveConectando, setLiveConectando] = useState(false);
  const [liveAtivo, setLiveAtivo] = useState(false);
  const [erro, setErro] = useState('');

  const recognitionRef = useRef<any>(null);
  const liveSocketRef = useRef<WebSocket | null>(null);
  const liveSetupCompletoRef = useRef(false);
  const liveInputTranscricaoRef = useRef('');
  const liveOutputTranscricaoRef = useRef('');
  const liveAudioContextRef = useRef<AudioContext | null>(null);
  const livePlaybackContextRef = useRef<AudioContext | null>(null);
  const livePlaybackQueueTimeRef = useRef(0);
  const liveMicStreamRef = useRef<MediaStream | null>(null);
  const liveMicSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const liveMicProcessorRef = useRef<ScriptProcessorNode | null>(null);

  useEffect(() => {
    async function carregar() {
      try {
        const { data } = await api.get<ConfigPublica>('/ia/publico/configuracao/1');
        setConfig(data);
        if (data?.mensagem_boas_vindas_publica) {
          setMensagens([{ id: 1, papel: 'assistente', texto: data.mensagem_boas_vindas_publica }]);
        }
      } catch {
        setErro('Não foi possível carregar o assistente público da creche.');
      }
    }
    carregar();
  }, []);

  const materiaisPublicos = useMemo(() => config?.materiais_publicos || [], [config]);

  const escolherVozFeminina = React.useCallback((): SpeechSynthesisVoice | null => {
    const vozes = window.speechSynthesis.getVoices();
    // Prioridade 1: voz configurada na creche
    if (config?.voz_publica) {
      const configurada = vozes.find((v) => v.name === config.voz_publica);
      if (configurada) return configurada;
    }
    // Prioridade 2: vozes femininas pt-BR por nome (Google, Edge TTS, macOS)
    const femininas = vozes.filter((v) =>
      v.lang.startsWith('pt') &&
      /female|feminino|\bf\b|francisca|vitoria|vitória|luciana|isabel|helena|leila|ana\b|maria\b|google.*pt.*female|edgetts.*pt.*female/i.test(v.name)
    );
    if (femininas.length) return femininas[0];
    // Prioridade 3: qualquer voz pt-BR
    const ptbr = vozes.filter((v) => v.lang === 'pt-BR' || v.lang === 'pt_BR');
    if (ptbr.length) return ptbr[0];
    // Prioridade 4: qualquer voz pt
    const pt = vozes.filter((v) => v.lang.startsWith('pt'));
    if (pt.length) return pt[0];
    return null;
  }, [config?.voz_publica]);

  function falar(texto: string) {
    if (!config?.audio_saida_publico || typeof window === 'undefined' || !window.speechSynthesis || liveAtivo) return;
    const executar = () => {
      const utterance = new SpeechSynthesisUtterance(texto);
      utterance.lang   = 'pt-BR';
      utterance.rate   = 0.92;
      utterance.pitch  = 1.2;
      utterance.volume = 1;
      const voz = escolherVozFeminina();
      if (voz) utterance.voice = voz;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utterance);
    };
    if (window.speechSynthesis.getVoices().length === 0) {
      window.speechSynthesis.addEventListener('voiceschanged', executar, { once: true });
    } else {
      executar();
    }
  }

  function toBase64(bytes: Uint8Array): string {
    let binary = '';
    for (let i = 0; i < bytes.length; i += 1) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  function fromBase64(base64: string): Uint8Array {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  function downsampleTo16k(input: Float32Array, inputSampleRate: number): Int16Array {
    if (inputSampleRate === 16000) {
      const out = new Int16Array(input.length);
      for (let i = 0; i < input.length; i += 1) {
        const s = Math.max(-1, Math.min(1, input[i]));
        out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
      }
      return out;
    }

    const ratio = inputSampleRate / 16000;
    const outputLength = Math.max(1, Math.round(input.length / ratio));
    const output = new Int16Array(outputLength);
    let outIndex = 0;
    let inputIndex = 0;

    while (outIndex < outputLength) {
      const nextInputIndex = Math.min(input.length, Math.round((outIndex + 1) * ratio));
      let sum = 0;
      let count = 0;
      for (let i = inputIndex; i < nextInputIndex; i += 1) {
        sum += input[i];
        count += 1;
      }
      const sample = count > 0 ? sum / count : input[inputIndex] || 0;
      const clamped = Math.max(-1, Math.min(1, sample));
      output[outIndex] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
      outIndex += 1;
      inputIndex = nextInputIndex;
    }

    return output;
  }

  function tocarPcm24k(base64Data: string) {
    const bytes = fromBase64(base64Data);
    if (bytes.length < 2) return;

    if (!livePlaybackContextRef.current) {
      livePlaybackContextRef.current = new AudioContext();
      livePlaybackQueueTimeRef.current = livePlaybackContextRef.current.currentTime;
    }

    const ctx = livePlaybackContextRef.current;
    const sampleCount = Math.floor(bytes.length / 2);
    const floatData = new Float32Array(sampleCount);
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    for (let i = 0; i < sampleCount; i += 1) {
      floatData[i] = view.getInt16(i * 2, true) / 0x8000;
    }

    const audioBuffer = ctx.createBuffer(1, sampleCount, 24000);
    audioBuffer.copyToChannel(floatData, 0);

    const source = ctx.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(ctx.destination);

    const startAt = Math.max(ctx.currentTime, livePlaybackQueueTimeRef.current);
    source.start(startAt);
    livePlaybackQueueTimeRef.current = startAt + audioBuffer.duration;
  }

  function encerrarLiveSessao() {
    try {
      if (liveSocketRef.current && liveSocketRef.current.readyState === WebSocket.OPEN) {
        liveSocketRef.current.send(JSON.stringify({ realtimeInput: { audioStreamEnd: true } }));
      }
    } catch {}

    try { liveSocketRef.current?.close(); } catch {}
    liveSocketRef.current = null;
    liveSetupCompletoRef.current = false;

    if (liveMicProcessorRef.current) {
      try { liveMicProcessorRef.current.disconnect(); } catch {}
      liveMicProcessorRef.current.onaudioprocess = null;
      liveMicProcessorRef.current = null;
    }

    if (liveMicSourceRef.current) {
      try { liveMicSourceRef.current.disconnect(); } catch {}
      liveMicSourceRef.current = null;
    }

    if (liveMicStreamRef.current) {
      liveMicStreamRef.current.getTracks().forEach((track) => track.stop());
      liveMicStreamRef.current = null;
    }

    if (liveAudioContextRef.current) {
      try { void liveAudioContextRef.current.close(); } catch {}
      liveAudioContextRef.current = null;
    }

    if (livePlaybackContextRef.current) {
      try { void livePlaybackContextRef.current.close(); } catch {}
      livePlaybackContextRef.current = null;
      livePlaybackQueueTimeRef.current = 0;
    }

    setLiveConectando(false);
    setLiveAtivo(false);
    setOuvindo(false);
  }

  async function iniciarLiveSessao() {
    if (!config?.audio_entrada_publico) {
      setErro('Áudio de entrada não está habilitado para atendimento público.');
      return;
    }

    setErro('');
    setLiveConectando(true);

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });

    const { data } = await api.post<LiveTokenResponse>('/ia/publico/live/token', { id_creche: 1 });
    if (!data?.ws_url) {
      throw new Error('Não foi possível iniciar sessão Live (token ausente).');
    }

    const socket = new WebSocket(data.ws_url);
    liveSocketRef.current = socket;

    socket.onopen = () => {
      const setup = data?.setup || {
        model: `models/${data.model || 'gemini-3.1-flash-live-preview'}`,
        generationConfig: { responseModalities: ['AUDIO'], temperature: 0.65 },
        inputAudioTranscription: {},
        outputAudioTranscription: {},
      };
      socket.send(JSON.stringify({ setup }));
    };

    socket.onmessage = (event) => {
      try {
        const mensagem = JSON.parse(String(event.data || '{}'));

        if (mensagem?.setupComplete !== undefined) {
          liveSetupCompletoRef.current = true;
          setLiveConectando(false);
          setLiveAtivo(true);
          setOuvindo(true);

          const audioContext = new AudioContext();
          const source = audioContext.createMediaStreamSource(stream);
          const processor = audioContext.createScriptProcessor(4096, 1, 1);

          processor.onaudioprocess = (audioEvent) => {
            if (!liveSetupCompletoRef.current || !liveSocketRef.current || liveSocketRef.current.readyState !== WebSocket.OPEN) return;
            const channelData = audioEvent.inputBuffer.getChannelData(0);
            const pcm16 = downsampleTo16k(channelData, audioContext.sampleRate);
            const pcmBytes = new Uint8Array(pcm16.buffer);
            const b64 = toBase64(pcmBytes);
            liveSocketRef.current.send(JSON.stringify({
              realtimeInput: {
                audio: {
                  data: b64,
                  mimeType: 'audio/pcm;rate=16000',
                },
              },
            }));
          };

          source.connect(processor);
          processor.connect(audioContext.destination);

          liveMicStreamRef.current = stream;
          liveAudioContextRef.current = audioContext;
          liveMicSourceRef.current = source;
          liveMicProcessorRef.current = processor;
        }

        const serverContent = mensagem?.serverContent;
        if (!serverContent) return;

        const inputTxt = String(serverContent?.inputTranscription?.text || '').trim();
        if (inputTxt) {
          liveInputTranscricaoRef.current = inputTxt;
        }

        const outputTxt = String(serverContent?.outputTranscription?.text || '').trim();
        if (outputTxt) {
          liveOutputTranscricaoRef.current = outputTxt;
        }

        const parts = serverContent?.modelTurn?.parts;
        if (Array.isArray(parts)) {
          for (const part of parts) {
            const inline = part?.inlineData;
            const mime = String(inline?.mimeType || '');
            const audioData = String(inline?.data || '');
            if (audioData && mime.includes('audio/pcm')) {
              tocarPcm24k(audioData);
            }
          }
        }

        if (serverContent?.turnComplete) {
          const userTexto = liveInputTranscricaoRef.current.trim();
          if (userTexto) {
            setMensagens((prev) => [...prev, { id: Date.now(), papel: 'usuario', texto: userTexto }]);
            liveInputTranscricaoRef.current = '';
          }

          const assistenteTexto = liveOutputTranscricaoRef.current.trim();
          if (assistenteTexto) {
            setMensagens((prev) => [...prev, { id: Date.now() + 1, papel: 'assistente', texto: assistenteTexto }]);
            liveOutputTranscricaoRef.current = '';
          }
        }
      } catch {
        // ignora mensagens malformadas
      }
    };

    socket.onerror = () => {
      setErro('Falha na conexão com a sessão de voz ao vivo.');
      encerrarLiveSessao();
    };

    socket.onclose = () => {
      encerrarLiveSessao();
    };
  }

  async function enviarPergunta(textoPergunta?: string) {
    const texto = (textoPergunta ?? pergunta).trim();
    if (!texto) return;

    const userMessage: Mensagem = { id: Date.now(), papel: 'usuario', texto };
    setMensagens((prev) => [...prev, userMessage]);
    setPergunta('');
    setCarregando(true);
    try {
      const { data } = await api.post('/ia/publico/chat', { id_creche: 1, pergunta: texto });
      const resposta = String(data?.resposta || 'Não consegui responder agora.');
      setMensagens((prev) => [...prev, { id: Date.now() + 1, papel: 'assistente', texto: resposta }]);
      falar(resposta);
      setErro('');
    } catch {
      setErro('Não foi possível responder sua pergunta agora.');
    } finally {
      setCarregando(false);
    }
  }

  async function iniciarAudioFallback() {
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Recognition || !config?.audio_entrada_publico) {
      setErro('Reconhecimento de voz não disponível neste navegador ou desabilitado pela creche.');
      return;
    }

    // Verifica permissão de microfone antes de iniciar
    if (navigator.permissions) {
      try {
        const status = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        if (status.state === 'denied') {
          setErro('Permissão para o microfone negada. Clique no ícone de cadeado na barra de endereços e permita o acesso ao microfone.');
          return;
        }
      } catch {
        // Navegador não suporta permissions.query — continua normalmente
      }
    }

    const recognition = new Recognition();
    recognition.lang = 'pt-BR';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onstart = () => { setOuvindo(true); setErro(''); };
    recognition.onend = () => setOuvindo(false);
    recognition.onerror = (event: any) => {
      setOuvindo(false);
      const tipo: string = event?.error ?? '';
      const mensagens: Record<string, string> = {
        'not-allowed':          'Permissão para o microfone negada. Clique no ícone de cadeado na barra de endereços e permita o acesso ao microfone.',
        'audio-capture':        'Microfone não encontrado ou indisponível. Verifique se um microfone está conectado.',
        'no-speech':            'Nenhuma fala detectada. Tente falar mais perto do microfone.',
        'network':              'Erro de conexão ao serviço de reconhecimento de voz. Verifique sua internet.',
        'aborted':              'Captura de áudio cancelada.',
        'service-not-allowed':  'Serviço de reconhecimento de voz não autorizado neste contexto.',
        'language-not-supported': 'Idioma não suportado pelo reconhecimento de voz do navegador.',
      };
      setErro(mensagens[tipo] ?? `Não foi possível capturar o áudio (${tipo || 'erro desconhecido'}).`);
    };
    recognition.onresult = (event: any) => {
      const texto = event?.results?.[0]?.[0]?.transcript || '';
      setPergunta(texto);
      void enviarPergunta(texto);
    };
    recognitionRef.current = recognition;
    recognition.start();
  }

  async function iniciarConversaVoz() {
    if (liveAtivo || liveConectando) {
      encerrarLiveSessao();
      return;
    }

    if (!config?.audio_entrada_publico) {
      setErro('Áudio de entrada desabilitado pela creche.');
      return;
    }

    try {
      await iniciarLiveSessao();
      return;
    } catch (err) {
      const detalhe = String((err as any)?.message || '');
      console.error('Falha no modo Live, aplicando fallback:', detalhe);
      setErro('Modo Live indisponível no momento. Usando modo de voz padrão.');
      await iniciarAudioFallback();
    }
  }

  useEffect(() => () => {
    encerrarLiveSessao();
    try {
      recognitionRef.current?.stop?.();
    } catch {}
  }, []);

  return (
    <div className="public-home">

      {/* ── HERO BANNER ── */}
      <header className="public-hero-banner">
        <div className="public-hero-content">
          <p className="public-kicker">✦ Atendimento inteligente da creche</p>
          <h1>{config?.creche?.nome || 'Creche'}</h1>
          <p className="public-subtitle">
            {config?.conteudo_site_publico || 'A creche oferece acolhimento, proposta pedagógica ativa, alimentação balanceada e acompanhamento próximo das famílias.'}
          </p>
          <div className="public-actions">
            <Link className="btn-primario" to="/login">Área interna</Link>
            <button className="btn-secundario" onClick={() => enviarPergunta('Quais são os horários de funcionamento da creche?')}>
              Perguntar horários
            </button>
          </div>
          <div className="public-topicos">
            <span className="public-topico-label">Tópicos liberados:</span>
            {(config?.topicos_publicos_liberados?.length
              ? config.topicos_publicos_liberados
              : ['horarios', 'cardapio', 'contato', 'atuacao', 'site']
            ).map((t) => (
              <span key={t} className="public-topico-badge">{t}</span>
            ))}
          </div>
        </div>

        <div className="public-summary-card">
          <h3 className="public-card-title">Informações rápidas</h3>
          <div className="public-info-row">
            <span className="public-info-icon">🕐</span>
            <div>
              <strong>Funcionamento</strong>
              <p>{config?.horario_funcionamento || 'Não informado.'}</p>
            </div>
          </div>
          <div className="public-info-row">
            <span className="public-info-icon">📞</span>
            <div>
              <strong>Contato</strong>
              <p>{config?.contato_publico || 'Não informado.'}</p>
            </div>
          </div>
          <div className="public-info-row">
            <span className="public-info-icon">🌱</span>
            <div>
              <strong>Atuação</strong>
              <p>{config?.formas_atuacao || 'Não informado.'}</p>
            </div>
          </div>
        </div>
      </header>

      {/* ── GRID PRINCIPAL: chat + cardápio ── */}
      <main className="public-main-grid">

        <article className="public-chat-card">
          <div className="public-chat-header">
            <div className="public-lia-avatar" aria-hidden="true">
              {(config?.nome_assistente || 'L')[0].toUpperCase()}
            </div>
            <div className="public-chat-title">
              <h2>{config?.nome_assistente || 'Assistente da Creche'}</h2>
              <p>Atendimento natural para famílias e público externo.</p>
            </div>
            <button
              className={`public-mic-btn${ouvindo ? ' public-mic-btn--ouvindo' : ''}`}
              onClick={iniciarConversaVoz}
              disabled={(!liveAtivo && (ouvindo || liveConectando)) || !config?.audio_entrada_publico}
              title={liveAtivo ? 'Encerrar conversa ao vivo' : (liveConectando ? 'Conectando...' : 'Falar com a Lia')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                   strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="23" />
                <line x1="8" y1="23" x2="16" y2="23" />
              </svg>
              <span>{liveAtivo ? 'Encerrar' : (liveConectando ? 'Conectando…' : (ouvindo ? 'Ouvindo…' : 'Falar ao vivo'))}</span>
            </button>
          </div>

          {erro && <div className="alerta alerta-erro">{erro}</div>}

          <div className="public-chat-history">
            {mensagens.map((mensagem) => (
              <article key={mensagem.id} className={`public-msg public-msg-${mensagem.papel}`}>
                <span className="public-msg-autor">
                  {mensagem.papel === 'usuario' ? 'Você' : config?.nome_assistente || 'Assistente'}
                </span>
                <p>{mensagem.texto}</p>
              </article>
            ))}
            {carregando && (
              <article className="public-msg public-msg-assistente">
                <span className="public-msg-autor">{config?.nome_assistente || 'Assistente'}</span>
                <p className="public-typing">
                  <span /><span /><span />
                </p>
              </article>
            )}
          </div>

          <div className="public-chat-input">
            <textarea
              rows={2}
              value={pergunta}
              onChange={(event) => setPergunta(event.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void enviarPergunta(); } }}
              placeholder="Ex.: Qual é o cardápio desta semana? (Enter para enviar)"
            />
            <button className="btn-primario" onClick={() => enviarPergunta()} disabled={carregando}>
              {carregando ? 'Respondendo…' : 'Enviar'}
            </button>
          </div>
        </article>

        <article className="public-info-card">
          <h3 className="public-card-title">🍽️ Cardápio semanal</h3>
          {(config?.cardapios || []).length === 0 ? (
            <p className="texto-ajuda">Cardápio ainda não publicado.</p>
          ) : (
            <div className="public-menu-list">
              {config?.cardapios?.map((item) => (
                <div key={item.id} className="public-menu-item">
                  <span className="public-menu-dia">{item.dia_semana || 'Dia'}</span>
                  {item.cafe_manha  && <span><strong>Café:</strong> {item.cafe_manha}</span>}
                  {item.almoco      && <span><strong>Almoço:</strong> {item.almoco}</span>}
                  {item.lanche_tarde && <span><strong>Lanche:</strong> {item.lanche_tarde}</span>}
                  {item.jantar      && <span><strong>Jantar:</strong> {item.jantar}</span>}
                </div>
              ))}
            </div>
          )}
        </article>
      </main>

      {/* ── GRID INFERIOR: proposta + materiais ── */}
      <div className="public-bottom-grid">
        <article className="public-info-card">
          <h3 className="public-card-title">🌿 Proposta pedagógica</h3>
          <p><strong>Como atuamos:</strong> {config?.formas_atuacao || 'Não informado.'}</p>
          <p><strong>Conteúdo institucional:</strong> {config?.conteudo_site_publico || 'Não informado.'}</p>
          <p><strong>Canais de contato:</strong> {config?.contato_publico || [config?.creche?.telefone, config?.creche?.email].filter(Boolean).join(' | ') || 'Não informado.'}</p>
        </article>

        <article className="public-info-card">
          <h3 className="public-card-title">📚 Materiais públicos aprovados</h3>
          {materiaisPublicos.length === 0 ? (
            <p className="texto-ajuda">Nenhum material público aprovado até o momento.</p>
          ) : (
            <div className="public-menu-list">
              {materiaisPublicos.map((material) => (
                <div key={material.id} className="public-menu-item">
                  <strong>{material.titulo}</strong>
                  <span>{material.descricao || 'Sem resumo validado.'}</span>
                  <span>Tipo: {material.tipo || '-'}</span>
                  {material.origem?.startsWith('http') ? (
                    <a href={material.origem} target="_blank" rel="noreferrer">Acessar origem</a>
                  ) : (
                    <span>Origem: {material.origem || '-'}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </article>
      </div>

    </div>
  );
}
